import { useEffect } from 'react'
import { useTranslations } from 'use-intl'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

export interface PopupOutcomeCopy {
  title: string
  message: string
  variant: 'default' | 'destructive'
}

/**
 * The page a provider's popup lands on after its capture-redirect has recorded the return
 * (`/bank/callback`, `/tax/callback`). It does no work: the tab that opened the popup is the one
 * that learns the outcome. Twin of apps/client's PopupOutcomeCard (FIN-709, FIN-980).
 *
 * Auto-closes only when this really is a popup another tab opened (`window.opener` set). Reached
 * by a full-page navigation instead — some mobile browsers do that — closing would strand the
 * user with no way back. The button is always rendered: some browsers only allow a
 * script-initiated close() on a window that script itself opened, and that can fail silently
 * even with window.opener set. Mirrors apps/client's CloseTabButton (FIN-575).
 */
export function PopupOutcomeCard({ copy }: { copy: PopupOutcomeCopy }) {
  const t = useTranslations('consultantOnboarding.app')
  useEffect(() => {
    if (window.opener) {
      window.close()
    }
  }, [])

  return (
    <main className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>{copy.title}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <Alert variant={copy.variant}>
            <AlertDescription>{copy.message}</AlertDescription>
          </Alert>
          <Button onClick={() => window.close()}>{t('popupClose')}</Button>
        </CardContent>
      </Card>
    </main>
  )
}
